import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components'; // Import css
import { Camera, Mic, Wifi, Check, Loader2, AlertCircle, RotateCcw, Info, Monitor } from './icons/AssessmentIcons';
import { COLORS } from '../theme';
import { Tooltip } from 'antd';
import { resolveDeviceTier, DEVICE_TIER } from '../utils/deviceTier';

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

// --- Styled Components ---

const Container = styled.div`
  padding: 30px;
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  position: relative;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${COLORS.HEADING};
  margin-bottom: 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${COLORS.TEXT_SECONDARY};
`;

const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CheckItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  opacity: 0;
  animation: ${fadeIn} 0.4s ease forwards;
  animation-delay: ${props => props.delay}s;
  transition: all 0.3s;
  
  ${props => props.status === 'success' && css`
    background: #f0fdf4;
    border-color: #bbf7d0;
  `}

  ${props => props.status === 'error' && css`
    background: #fef2f2;
    border-color: #fecaca;
  `}
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  ${props => props.status === 'success' && css`color: #059669;`}
  ${props => props.status === 'error' && css`color: #dc2626;`}
  ${props => props.status === 'pending' && css`color: #3b82f6;`}
`;

const LabelGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.span`
  font-weight: 600;
  font-size: 15px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StatusLabel = styled.span`
  font-size: 12px;
  color: #6b7280;
  
  ${props => props.status === 'pending' && css`
     color: #3b82f6; 
     animation: ${pulse} 1.5s infinite;
  `}
`;

const StatusIcon = styled.div`
   display: flex;
   align-items: center;
   justify-content: center;
   width: 24px;
   height: 24px;
`;

const RetryButton = styled.button`
  margin-top: 20px;
  padding: 10px 20px;
  background: ${COLORS.PRIMARY};
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  margin-right: auto;
  transition: background 0.2s;

  &:hover {
    background: ${COLORS.BLUE_S_30};
  }
`;

const InfoButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
    color: ${COLORS.TEXT_SECONDARY};
    
    &:hover {
        color: ${COLORS.PRIMARY};
    }
`;

const STRICT_MODE = true; // Set to false to allow bypassing checks

const ConfigCheck = ({ onAllChecksComplete }) => {
  const [checks, setChecks] = useState({
    os: { status: 'pending', label: 'Checking device...' },
    internet: { status: 'waiting', label: 'Waiting...' },
    camera: { status: 'waiting', label: 'Waiting...' },
    mic: { status: 'waiting', label: 'Waiting...' }
  });
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    runChecks();
  }, []);

  const runChecks = async () => {
    setHasError(false);
    // Reset
    setChecks({
      os: { status: 'pending', label: 'Checking device...' },
      internet: { status: 'waiting', label: 'Waiting...' },
      camera: { status: 'waiting', label: 'Waiting...' },
      mic: { status: 'waiting', label: 'Waiting...' }
    });

    let failed = false;

    // 0. Device capability tier check.
    // We no longer hard-block all iOS devices. Instead we resolve a proctoring
    // capability tier (see utils/deviceTier.js): macOS Safari (FULL), iPad
    // (STANDARD) and iPhone (LITE) are all allowed; only in-app browsers
    // (Gmail/LinkedIn/WhatsApp WKWebViews) are BLOCKED because getUserMedia is
    // unavailable there and the camera/mic checks below would silently fail.
    await new Promise(r => setTimeout(r, 500));
    const tier = resolveDeviceTier();

    if (tier === DEVICE_TIER.BLOCKED) {
      setChecks(prev => ({
        ...prev,
        os: { status: 'error', label: 'Please open this link in Safari/Chrome' }
      }));
      failed = true;
    } else {
      const label =
        tier === DEVICE_TIER.LITE
          ? 'Supported (Phone mode)'
          : 'Supported Device';
      setChecks(prev => ({
        ...prev,
        os: { status: 'success', label },
        internet: { status: 'pending', label: 'Checking connection...' }
      }));
    }

    if (failed) { setHasError(true); return; }

    // 1. Internet
    await new Promise(r => setTimeout(r, 800)); // Fake network delay
    // We assume internet is fine if they loaded the app, but could add navigator.onLine check
    if (!navigator.onLine) {
      setChecks(prev => ({ ...prev, internet: { status: 'error', label: 'No Internet Connection' } }));
      failed = true;
    } else {
      setChecks(prev => ({
        ...prev,
        internet: { status: 'success', label: 'Connected' },
        camera: { status: 'pending', label: 'Checking permissions...' }
      }));
    }

    if (failed) { setHasError(true); return; }

    // 2. Camera
    try {
      await navigator.mediaDevices.getUserMedia({ video: true });
      setChecks(prev => ({
        ...prev,
        camera: { status: 'success', label: 'Accessible' },
        mic: { status: 'pending', label: 'Checking permissions...' }
      }));
    } catch (e) {
      setChecks(prev => ({
        ...prev,
        camera: { status: 'error', label: 'Access Denied' },
        mic: { status: 'waiting', label: 'Waiting...' }
      }));
      failed = true;
    }

    if (failed) { setHasError(true); return; }

    // 3. Mic
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setChecks(prev => ({
        ...prev,
        mic: { status: 'success', label: 'Accessible' }
      }));
    } catch (e) {
      setChecks(prev => ({
        ...prev,
        mic: { status: 'error', label: 'Access Denied' }
      }));
      failed = true;
    }

    if (failed) {
      setHasError(true);
      return;
    }

    // Complete
    setTimeout(() => {
      onAllChecksComplete();
    }, 1000);
  };

  const renderIcon = (type, status) => {
    switch (type) {
      case 'os': return <Monitor size={20} />;
      case 'internet': return <Wifi size={20} />;
      case 'camera': return <Camera size={20} />;
      case 'mic': return <Mic size={20} />;
      default: return null;
    }
  };

  return (
    <Container>
      <Header>
        <Title>System Check</Title>
        <Subtitle>Ensuring your device is ready for the assessment</Subtitle>
      </Header>

      <CheckList>
        {Object.entries(checks).map(([key, data], index) => (
          <CheckItem key={key} delay={index * 0.1} status={data.status}>
            <LeftSide>
              <IconBox status={data.status}>
                {renderIcon(key)}
              </IconBox>
              <LabelGroup>
                <Label>
                  {key === 'os' ? 'Operating System' : key.charAt(0).toUpperCase() + key.slice(1)}
                  {data.status === 'error' && (key === 'camera' || key === 'mic') && (
                    <Tooltip title="Click the lock or info icon in your browser address bar to allow access.">
                      <InfoButton>
                        <Info size={14} />
                      </InfoButton>
                    </Tooltip>
                  )}
                </Label>
                <StatusLabel status={data.status}>{data.label}</StatusLabel>
              </LabelGroup>
            </LeftSide>
            <StatusIcon>
              {data.status === 'pending' && <Loader2 size={18} className="spin" />}
              {data.status === 'success' && <Check size={18} color="#059669" />}
              {data.status === 'error' && <AlertCircle size={18} color="#dc2626" />}
            </StatusIcon>
          </CheckItem>
        ))}
      </CheckList>

      {hasError && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
          <RetryButton onClick={runChecks} style={{ margin: 0 }}>
            <RotateCcw size={16} /> Retry Check
          </RetryButton>
          {!STRICT_MODE && (
            <RetryButton onClick={onAllChecksComplete} style={{ margin: 0, background: COLORS.TEXT_SECONDARY }}>
              Continue Anyway
            </RetryButton>
          )}
        </div>
      )}
    </Container>
  );
};

export default ConfigCheck;